import { useState, useEffect, useRef } from "react";
import axios from "axios";
import BlockedPage from "../pages/BlockedPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initial blocking state - prevents any flash by defaulting to blocked
const INITIAL_STATE = {
  checked: false,
  blocked: true, // Default to blocked to prevent content flash
  targetingData: null,
  ssrHtml: null, // Server-side rendered HTML for blocked users
};

const TargetingWrapper = ({ children }) => {
  const [state, setState] = useState(INITIAL_STATE);
  const checkStarted = useRef(false);

  useEffect(() => {
    // Prevent double-checking in strict mode
    if (checkStarted.current) return;
    checkStarted.current = true;
    
    checkTargeting();
  }, []);

  const checkTargeting = async () => {
    try {
      // Capture original referrer (e.g. google.com) before any client-side
      // navigation. This is needed because axios fetches use the page itself
      // as referer, not the original arrival source.
      const originalReferrer = document.referrer || "";

      // First, try to get SSR page (this returns full HTML for blocked users)
      const ssrRes = await axios.get(`${API}/ssr/page`, {
        validateStatus: (status) => status < 500,
        headers: { "X-Original-Referrer": originalReferrer },
      });
      
      // If SSR returns HTML content, use it (user is blocked)
      if (ssrRes.status === 200 && ssrRes.data && typeof ssrRes.data === 'string') {
        setState({
          checked: true,
          blocked: true,
          targetingData: { reason: "ssr_blocked" },
          ssrHtml: ssrRes.data,
        });
        return;
      }
      
      // If SSR returns 204 (no content), user is allowed - verify with targeting check
      const res = await axios.get(`${API}/targeting/check`, {
        headers: { "X-Original-Referrer": originalReferrer },
      });
      const data = res.data;
      
      // If blocked, show blocked page
      if (data.blocked) {
        setState({
          checked: true,
          blocked: true,
          targetingData: data,
          ssrHtml: null,
        });
        return;
      }
      
      // Handle mobile redirect for allowed users
      if (data.reason === "allowed" || !data.blocked) {
        try {
          const settingsRes = await axios.get(`${API}/targeting/settings`);
          const settings = settingsRes.data;
          
          if (settings.mobile_redirect_enabled && settings.mobile_redirect_url) {
            const ua = navigator.userAgent.toLowerCase();
            const isMobile = /iphone|ipod|android|blackberry|opera mini|iemobile|mobile/i.test(ua);
            
            if (isMobile && !window.location.hostname.startsWith('m.')) {
              const currentUrl = new URL(window.location.href);
              const mobileUrl = new URL(settings.mobile_redirect_url);
              
              if (currentUrl.hostname !== mobileUrl.hostname) {
                const redirected = sessionStorage.getItem('mobile_redirected');
                if (!redirected) {
                  sessionStorage.setItem('mobile_redirected', 'true');
                  window.location.href = settings.mobile_redirect_url;
                  return;
                }
              }
            }
          }
        } catch (settingsError) {
          console.warn("Error fetching targeting settings:", settingsError);
        }
      }
      
      // Allowed - show content
      setState({
        checked: true,
        blocked: false,
        targetingData: data,
        ssrHtml: null,
      });
    } catch (error) {
      console.error("Error checking targeting:", error);
      // On error, allow access (fail open)
      setState({
        checked: true,
        blocked: false,
        targetingData: { allowed: true, reason: "error" },
        ssrHtml: null,
      });
    }
  };

  // Before check completes, show nothing (brief moment, not visible)
  if (!state.checked) {
    return null;
  }

  // If we have SSR HTML, render it directly (this is what search engines see)
  if (state.ssrHtml) {
    // Replace the entire document with SSR HTML
    document.open();
    document.write(state.ssrHtml);
    document.close();
    return null;
  }

  // If blocked, show blocked page
  if (state.blocked) {
    return <BlockedPage reason={state.targetingData?.reason} targetingData={state.targetingData} />;
  }

  // If allowed, show children
  return children;
};

export default TargetingWrapper;
